/* 'Manage sets' for saved hashtag sets (Part E, T31): rename a set, change
 * its hashtags, delete it, and the LinkedIn / Facebook trimming setting.
 * Opened from the composer's '# Hashtags' panel (as a dialog) and shown on
 * Settings › Hashtags (inline). API: /api/hashtags/sets/:id and
 * /api/hashtags/settings (docs/MOBILE_API.md › Hashtags).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';

import { uniqueHashtags, cleanTag, INSTAGRAM_LIMIT, TRIM_KEEP } from '../../../../utils/hashtags';

const ax = axios.create({ withCredentials: true });

const S = {
  row: { border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  input: { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  textarea: { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit', outline: 'none', resize: 'vertical', minHeight: 60 },
  primary: { padding: '6px 12px', borderRadius: 8, border: 0, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  ghost: { padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  danger: { padding: '6px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  note: { fontSize: 12.5, color: '#6b7280' },
  error: { fontSize: 12.5, color: '#dc2626' },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6b7280' },
};

// What the user typed in the hashtags box → a list (words without '#' count too).
const parseTags = text => {
  const words = String(text || '').split(/[\s,]+/).filter(Boolean);
  const withHash = uniqueHashtags(words.map(w => (w.startsWith('#') ? w : `#${w}`)).join(' '));
  const bad = words.filter(w => !cleanTag(w));
  return { tags: withHash, bad };
};

function SetRow ({ set, onSaved, onDeleted }) {
  const [name, setName] = useState(set.name);
  const [text, setText] = useState(set.tags.join(' '));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const { tags, bad } = parseTags(text);
  const changed = name.trim() !== set.name || tags.join(' ') !== set.tags.join(' ');

  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      const { data } = await ax.put(`/api/hashtags/sets/${set.id}`, { name, tags });
      onSaved(data.set);
      setName(data.set.name);
      setText(data.set.tags.join(' '));
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save the set. Try again in a moment.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError('');
    try {
      await ax.delete(`/api/hashtags/sets/${set.id}`);
      onDeleted(set.id);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not delete the set. Try again in a moment.');
      setBusy(false);
    }
  };

  return (
    <div style={S.row} data-testid='hashtag-set-row'>
      <input style={S.input} aria-label={`Name of the set ${set.name}`} value={name} maxLength={40} onChange={e => { setName(e.target.value); setSaved(false); }} />
      <textarea style={S.textarea} aria-label={`Hashtags in ${set.name}`} value={text} onChange={e => { setText(e.target.value); setSaved(false); }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...S.note, color: tags.length > INSTAGRAM_LIMIT ? '#dc2626' : '#6b7280', marginRight: 'auto' }}>
          {tags.length} of {INSTAGRAM_LIMIT} hashtags{bad.length ? ` · not hashtags: ${bad.slice(0, 3).join(', ')}` : ''}{saved ? ' · Saved' : ''}
        </span>
        {confirm ? (
          <>
            <span style={S.note}>Delete this set?</span>
            <button type='button' style={S.danger} disabled={busy} onClick={remove}>Yes, delete</button>
            <button type='button' style={S.ghost} onClick={() => setConfirm(false)}>No</button>
          </>
        ) : (
          <>
            <button type='button' style={S.danger} disabled={busy} onClick={() => setConfirm(true)}>Delete</button>
            <button type='button' style={{ ...S.primary, opacity: changed ? 1 : 0.5 }} disabled={!changed || busy || !tags.length || tags.length > INSTAGRAM_LIMIT} onClick={save}>Save changes</button>
          </>
        )}
      </div>
      {error && <div style={S.error} role='alert'>{error}</div>}
    </div>
  );
}

/** The manager itself: the trimming setting, then every set. */
export function HashtagSetList ({ sets, setSets, available = true, settings, onSettingsChange }) {
  const [settingError, setSettingError] = useState('');
  const [busy, setBusy] = useState(false);
  const on = !settings || settings.trimLinkedInFacebook !== false;

  const toggle = async () => {
    setBusy(true); setSettingError('');
    try {
      const { data } = await ax.put('/api/hashtags/settings', { trimLinkedInFacebook: !on });
      if (onSettingsChange) onSettingsChange(data);
    } catch (err) {
      setSettingError(err?.response?.data?.message || 'Could not save the setting. Try again in a moment.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={S.label}>LinkedIn and Facebook</span>
        <label className='form-check form-switch' style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: 0, paddingLeft: '2.5em' }}>
          <input className='form-check-input' type='checkbox' role='switch' checked={on} disabled={busy} onChange={toggle}
            aria-label='Trim hashtags on LinkedIn and Facebook' />
          <span style={{ fontSize: 13.5, color: '#374151' }}>
            <strong style={{ fontWeight: 600 }}>Trim hashtags on LinkedIn and Facebook.</strong>{' '}
            Keep only the first {TRIM_KEEP.linkedin} on LinkedIn and the first {TRIM_KEEP.facebook} on Facebook, so posts don't look spammy. Every other network gets them all.
          </span>
        </label>
        {settingError && <div style={S.error} role='alert'>{settingError}</div>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={S.label}>Your saved sets</span>
        {!available ? (
          <div style={S.note}>Saving hashtag sets is coming soon.</div>
        ) : !sets.length ? (
          <div style={S.note}>No saved sets yet. In Post, add hashtags to your caption, open # Hashtags and choose “Save current hashtags as a set”.</div>
        ) : sets.map(set => (
          <SetRow key={set.id} set={set}
            onSaved={next => setSets(prev => prev.map(s => (s.id === next.id ? next : s)).sort((a, b) => a.name.localeCompare(b.name)))}
            onDeleted={id => setSets(prev => prev.filter(s => s.id !== id))} />
        ))}
      </div>
    </div>
  );
}

export default function HashtagSetManager ({ show, onHide, ...rest }) {
  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>Manage hashtag sets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HashtagSetList {...rest} />
      </Modal.Body>
    </Modal>
  );
}
